<template>
  <div
    v-if="
      $auth.loggedIn ||
        isInchAssortmentCategory ||
        (currentFilters && currentFilters.length > 0)
    "
    class="category-filter-container pure-form"
  >
    <h3
      class="category-filter-header h3"
    >
      {{ $tv('filters', 'categories') }}
    </h3>
    <div
      class="category-filter-container__filters"
      v-if="facets"
    >
      <div
        v-for="(filter, index) in currentFilters"
        :key="filter.id"
        :class="[
          filter.isCoreFilter ? 'coreFilter' : 'noCoreFilter',
          'category-filter-container__filters__filter',
        ]"
      >
        <template v-if="isTypeDropdown(filter.type)">
          <DropDown
            :filter-values="getFilterValuesFromFacets(filter)"
            :label="filter.label"
            :title="filter.title"
            :selected-values="filter.values"
            :identifier="index"
            :disabled="isFilterDisabled(filter)"
            :has-info-icon="filter.hasInfoIcon"
            :is-sorted="true"
            @input="categoryChange($event, filter)"
            v-show="filter.isCoreFilter || showAllFilters"
          />
        </template>
        <template v-if="isTypeString(filter.type) || isTypeMultiInteger(filter.type) || isTypeMultiString(filter.type)">
          <MultiSelect
            :filter-values="getFilterValuesFromFacets(filter)"
            :label="filter.label"
            :title="filter.title"
            :selected-values="filter.values"
            :identifier="index"
            :disabled="filter.isDisabled"
            :has-info-icon="filter.hasInfoIcon"
            :is-sorted="true"
            @input="filter.values = $event"
            v-show="filter.isCoreFilter || showAllFilters"
          />
        </template>
        <template v-if="isTypeNumber(filter.type) || isTypeMultiDouble(filter.type)">
          <Range
            :filter-values="getFilterValuesFromFacets(filter)"
            :format="filter.type"
            :label="filter.label"
            :title="filter.title"
            :selected-values="filter.values"
            :identifier="index"
            :disabled="filter.isDisabled"
            :localized-measure="filter.localizedUnitOfMeasure"
            @input-value="filter.values = $event"
            v-show="filter.isCoreFilter || showAllFilters"
          />
        </template>
      </div>
      <div
        class="category-filter-container__filters__toggleSwitch coreFilter"
        v-if="isInchAssortmentCategory"
      >
        <div class="filter-item-wrapper">
          <ToggleSwitch
            :is-checked="inchAssortmentOnly"
            :label="$tv('useInchAssortmentOnly', 'product')"
            @change-value="setInchAssortmentOnly"
          />
        </div>
      </div>

      <div
        v-for="filter in currentFilters.filter(f => isTypeBoolean(f.type) && f.shortName === 'pricingFlag')"
        :key="filter.id"
        class="category-filter-container__filters__toggleSwitch coreFilter"
      >
        <div class="filter-item-wrapper">
          <ToggleSwitch
            :is-checked="filter.values"
            :label="$tv(filter.shortName, 'product')"
            @change-value="filter.values = !filter.values"
          />
        </div>
      </div>
    </div>
    <div
      :class="{
        'no-core-filters':
          !currentFilters ||
          currentFilters.filter((f) => !f.isCoreFilter).length <= 0,
      }"
      class="category-filter-container__button-container"
    >
      <kbutton
        class="button__secondary--showMore"
        :theme-color="'base'"
        @click="displayAllFilter"
      >
        <span
          class="subline"
        >
          {{ $tv(buttonText) }}
        </span>
        <NavigationExpandMore
          class="expand-more-icon"
          :class="buttonClass"
        />
      </kbutton>
      <kbutton
        v-if="isMobile && currentFilters && currentFilters.length > 0"
        :theme-color="'base'"
        ref="resetFilters"
        @click="resetFilters(null)"
      >
        <span
          class="subline"
        >{{ $tv('reset-filter', 'categories') }}</span>
      </kbutton>
      <kbutton
        v-if="currentFilters && currentFilters.length > 0"
        :theme-color="'primary'"
        class="button button__primary button--apply-filters"
        @click="applyFilterHandler"
      >
        <span class="subline">
          {{ $tv("apply-filter", "categories") }}
        </span>
      </kbutton>
    </div>
    <div
      class="category-filter-container__label-container"
      v-show="
        currentFilters.filter((f) => f.isFilteredBy).length ||
          appliedInchAssortmentOnly
      "
    >
      <h3
        v-if="isMobile"
      >
        {{ $tv('active', 'categories') }}
      </h3>
      <Labels
        :filters="currentFilters"
        :localized-measure="showInchValues ? 'inch' : 'mm'"
        :inch-text="appliedInchAssortmentOnly"
        @resetInput="resetFilters"
        @resetInchInput="resetInchFilter"
        @activeFilters="updateActiveFilterCount"
      />
    </div>
  </div>
</template>

<script>
import {
  ATTRIBUTE_IDENTIFIER_NEW,
  isNoAttributeFilter,
  DEFAULT_VALUES,
  VALUE_TYPES,
  setFilterValues
} from '~/assets/js/filters'
import {sanitizeHtml} from '~/assets/js/utils'
import {NON_ATTRIBUTE_FILTERS} from '~/assets/js/constants'
import {Button} from '@progress/kendo-vue-buttons'
import {useUserStore} from '@/store/user'
import MultiSelect from './AttributeFilterMultiSelect.vue'
import DropDown from './AttributeFilterDropDown.vue'
import Range from './AttributeFilterRange.vue'
import Labels from './AttributeFilterLabel.vue'
import ToggleSwitch from '~/components/input-components/ToggleSwitch.vue'
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'
import {storeToRefs} from 'pinia'
import {mapProductAttributeNames} from '~/assets/js/utils/product_attributes_mapping'

export default {
  name: 'CategoryFilter',
  components: {
    kbutton: Button,
    MultiSelect,
    DropDown,
    Range,
    Labels,
    ToggleSwitch,
    NavigationExpandMore
  },
  props: {
    filters: {
      type: Array || null,
      required: true,
      default() {
        return []
      }
    },
    facets: {
      type: Object || null,
      required: true,
      default() {
        return null
      }
    },
    productCount: {
      type: Number,
      default() {
        return null
      }
    },
    isMobile: {
      type: Boolean,
      default() {
        return false
      }
    },
    isInchAssortmentCategory: {
      type: Boolean,
      default() {
        return false
      }
    }
  },
  setup() {
    const {showInchValues, settings} = storeToRefs(useUserStore())
    const {updateSettings} = storeToRefs(useUserStore())
    const i18n = useI18n()
    return {
      showInchValues,
      userSettings: settings,
      updateSettings,
      i18n
    }
  },
  data() {
    return {
      showAllFilters: false,
      buttonText: 'categories.show-more-button',
      buttonClass: '',
      filterList: [],
      activeFilterCount: 0,
      inchAssortmentOnly: false,
      appliedInchAssortmentOnly: false
    }
  },
  watch: {
    productCount: {
      immediate: true,
      handler() {
        this.filterList = [...this.filters]
        this.setLocalizedFilters()
      }
    },
    showInchValues: {
      immediate: true,
      handler() {
        this.setLocalizedFilters()
      }
    }
  },
  computed: {
    currentFilters() {
      return this.filterList
    }
  },
  methods: {
    updateActiveFilterCount(val) {
      this.activeFilterCount = val
    },
    setLocalizedFilters() {
      this.filterList.forEach((filter) => {
        let shortName = this.$tv(
          filter.shortName,
          'attributesShort',
          'shortNameNotAvailable'
        )
        let longName = this.$tv(filter.shortName, 'attributes')
        let label = longName
        let title = longName
        filter.hasInfoIcon =
          filter.shortName.toLowerCase() ===
          NON_ATTRIBUTE_FILTERS.BRAND.toLowerCase()

        if (shortName !== 'shortNameNotAvailable' && shortName !== label) {
          label += ' (' + shortName + ')'
          title += ' (' + shortName + ')'
        }
        let localizedUnitOfMeasure = filter.unitOfMeasure
        if (filter.unitOfMeasure) {
          if (this.showInchValues && filter.unitOfMeasure === 'mm') {
            localizedUnitOfMeasure = 'inch'
          }
          label += ' [' + localizedUnitOfMeasure + ']'
          title = this.$te('product.attributeFullName')
            ? this.$tv(
              {
                messageKey: 'attributeFullName',
                messageValues: {
                  fullName: title,
                  unitOfMeasure: localizedUnitOfMeasure
                }
              },
              'product'
            )
            : title + ' [' + localizedUnitOfMeasure + ']'
        }

        if (filter.shortName === NON_ATTRIBUTE_FILTERS.BRAND) {
          filter.translateLabel = true
          filter.customTranslatePrefix = 'brands'
        }

        title = sanitizeHtml(title, window?.document)
        filter.label = label
        filter.localizedUnitOfMeasure = localizedUnitOfMeasure
        filter.title = title
      })
    },
    applyFilterHandler() {
      this.appliedInchAssortmentOnly = this.inchAssortmentOnly
      this.$emit('filterInchAssortmentOnly', this.appliedInchAssortmentOnly)
      this.$emit('applyFilters', this.currentFilters)
    },
    displayAllFilter() {
      this.showAllFilters = !this.showAllFilters
      this.buttonText = this.showAllFilters
        ? 'categories.show-less-button'
        : 'categories.show-more-button'
      this.buttonClass = this.showAllFilters ? 'open-icon' : ''
    },
    emitter(emitText, val) {
      this.$emit(emitText, val)
    },
    setInchAssortmentOnly() {
      this.inchAssortmentOnly = !this.inchAssortmentOnly
    },
    resetFilters(labels) {
      if (labels) {
        let filter = this.filterList.filter((val) => !labels.includes(val))
        const index = this.filterList.findIndex(
          (el) => el.shortName === filter[0].shortName
        )
        if (index !== -1) {
          this.filterList[index].values = setFilterValues(
            this.filterList[index].type
          )
          this.filterList[index].isActive = false
          this.filterList[index].isFilteredBy = false
          this.filterList[index].activeValues = null
        }
      } else {
        this.filterList.forEach((filter) => {
          if (!filter.isDisabled) {
            filter.values = setFilterValues(filter.type)
            filter.isActive = false
            filter.isFilteredBy = false
            filter.activeValues = null
          }
        })
        this.resetInchFilter()
      }
      this.$emit('applyFilters', this.filterList)
    },
    resetInchFilter() {
      this.inchAssortmentOnly = false
      this.appliedInchAssortmentOnly = false
      this.$emit('filterInchAssortmentOnly', this.appliedInchAssortmentOnly)
    },
    getFilterValuesFromFacets(filter) {
      let prop = ATTRIBUTE_IDENTIFIER_NEW + filter.shortName
      let translatable = true
      if (isNoAttributeFilter(filter.shortName, true)) {
        prop = mapProductAttributeNames(filter.shortName)
      } else {
        filter.customTranslatePrefix =
          'attributes.' + filter.shortName + '-values'
        translatable = this.i18n.messages.value[this.i18n.locale.value]?.attributes &&
          typeof this.i18n.messages.value[this.i18n.locale.value]?.attributes[
            filter?.shortName + '-values'
          ] === 'object'
      }
      const facets = this.facets[prop] ?? []
      if (facets && translatable) {
        facets.forEach((facet) => {
          const translationPrefix = filter.customTranslatePrefix
            ? filter.customTranslatePrefix
            : DEFAULT_VALUES.FILTER_TRANSLATE_PREFIX
          facet.label = this.$tv(facet.value, translationPrefix)
        })
      }
      return facets
    },
    isTypeMultiString(type) {
      return type === VALUE_TYPES.MULTI_STRING
    },
    isTypeMultiDouble(type) {
      return type === VALUE_TYPES.MULTI_DOUBLE
    },
    isTypeMultiInteger(type) {
      return type === VALUE_TYPES.MULTI_INTEGER
    },
    isTypeString(type) {
      return type === VALUE_TYPES.STRING
    },
    isTypeDropdown(type) {
      return type === VALUE_TYPES.DROPDOWN
    },
    isTypeNumber(type) {
      return type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT
    },
    isTypeBoolean(type) {
      return type === VALUE_TYPES.BOOLEAN
    },
    isFilterDisabled(filter) {
      const facets = this.getFilterValuesFromFacets(filter)
      if (facets.length === 1 && !filter.activeValues && !filter.isDisabled) {
        return facets[0].count === this.productCount
      }
      return filter.isDisabled ? true : facets.length === 0
    },
    categoryChange(event, filter) {
      if (event && Object.keys(event).length > 0 && !Array.isArray(event)) {
        filter.values = event
        this.emitter('categoryChange', event)
      }
    }
  }
}
</script>

<style lang="scss">
.category-filter-container {
  position: relative;
  padding: 0 $default-margin-padding-2x;
  background-color: $color__content--background__secondary;

  .category-filter-header {
    @include breakpoint-down($md) {
      margin-top: 0;
      width: calc(100% - 1.75rem);
    }
  }

  .h3 {
    margin-top:0;
  }

  h3 {
    @include breakpoint-down($md) {
      order: 1;
    }
  }

  &__filters {
    $gap: 1.25rem;

    display: flex;
    flex-flow: row wrap;
    margin: -1.875rem 0 0 -#{$gap};
    width: calc(100% + #{$gap});

    .coreFilter {
      order: 1;
    }

    .noCoreFilter {
      order: 2;
    }

    &__toggleSwitch {
      display: flex;
      align-items: flex-end;

      .filter-item-wrapper {
        display: block;
        min-height: 2.375rem;

        &:deep(label) {
          overflow: inherit;
        }

        .toggle-switch-container .k-switch  {
          margin: 0 .5rem 0 0;
        }
      }

      .switch {
        margin: .325rem 0;
      }
    }

    &__filter,
    &__toggleSwitch {
      .filter-item-wrapper {
        width: 11.825rem;
        margin: 1.875rem 0 0 #{$gap};

        @include breakpoint-down($md) {
          width: auto;
        }
      }

      .shows-stock-items {
        width: 11.825rem;
        margin-top: 3.3rem;
        padding-left: .5rem;

        .toggle-switch-container {
          font-weight: $base-text-font-weight-light;
        }
      }

      @include breakpoint-down($md) {
        flex-basis: 50%;
        min-width: 0;
      }
    }

    @include breakpoint-down($md) {
      $gap: 1.75rem;

      margin: -1.875rem 0 0 -#{$gap};
      width: calc(100% + #{$gap});
      order: 3;

      &__filter .filter-item-wrapper {
        margin: 1.875rem 0 0 #{$gap};
      }
    }
  }

  &__button-container {
    margin-top: 2.8125rem;
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;

    .k-button {
      padding-right: .5rem;
      margin-bottom: $default-margin-padding-width;
    }

    .expand-more-icon {
      font-size: 1.5rem;
    }

    .button__secondary--showMore {
      // padding: 0;

      .k-button-text {
        display: flex;
        justify-content: space-between;
        padding-right: .5rem;
      }

      @include breakpoint-down($md) {
        width: 100% !important;
        margin-bottom: 2.375rem;
        justify-content: center;
      }
    }

    &.no-core-filters {
      @include breakpoint-up($md) {
        .button--apply-filters {
          margin-left: auto;
        }
      }

      .button__secondary--showMore {
        display: none;
      }
    }

    @include breakpoint-down($md) {
      order: 4;
    }
  }

  &__label-container {
    @include breakpoint-down($md) {
      order: 2;
      background-color: $color__content--background__secondary;
      margin-bottom: 1.5625rem;
      margin-left: -.9375rem;
      margin-right: -.9375rem;
      padding: 0 .9375rem 1.25rem;
    }
  }

  @include breakpoint-down($md) {
    padding: 0;
    background-color: $color__content--background__primary;
    display: flex;
    flex-flow: column;
    flex-wrap: wrap;
    width: 100%;
  }

  .subline {
    margin-top: auto;
    margin-bottom: auto;
    margin-right: $default-margin-padding-width-small;
    font-weight: $base-text-font-weight-semi-bold;
  }
}
</style>
